import React, {useEffect, useState} from "react";
import { useRouter } from "next/router";
import YoutubeThumbnail from "@/components/AudioVideoComponents/YoutubeThumbnailComponent";
import ImageWrapper from "@/components/ImageComponent/ImageWrapper";
import {getBlogBySlug, postGlobalLeadCapture} from "@/pages/api";
import {PortableText} from "@portabletext/react";
import ImageBox from "@/components/HomePoojaComponents/HomePoojaImageBox";
import Head from "next/head";
import {DEFAULT_SEO_VALUES} from "@/commons/constants";
import {urlFor, toProxiedUrl} from "@/sanity-client/sanity";
import {
  createCRMLeadEnquiryBody,
  formatBlogsForm,
  getCookieItem,
  transformFields
} from "@/utils/utils";
import toast from "react-hot-toast";
import {BACKEND_API_ROUTE, POOJA_LEADS} from "@/commons/api_routes";
import {POSTREQUESTAPI} from "@/pages/api/apiUtils";
import SelectFieldset from "@/components/InputComponent/SelectComponent";
import MultiSelectFieldset from "@/components/InputComponent/MultiSelectComponent";
import InputTextComponent from "@/components/InputComponent/InputTextComponent";
import InputNumberComponent from "@/components/InputComponent/InputNumberComponent";
import InputDateComponent from "@/components/InputComponent/InputDateComponent";
import InputFileComponent from "@/components/InputComponent/InputFileComponent";
import InputDateTimeComponent from "@/components/InputComponent/InputDateTimeComponent";
import InputTextAreaComponent from "@/components/InputComponent/InputTextAreaComponent";
import { Phone, MessageCircle, ExternalLink } from "lucide-react";
const components = {
  types: {},
  marks: {
    link: ({ children, value }) => (
      <a
        title={children}
        href={value?.href}
        target={value?.target || "_self"}
        rel={value?.rel || "noopener noreferrer"}
        className="text-blue-600 hover:text-blue-800 underline decoration-2 underline-offset-2 transition-colors duration-200"
      >
        {children}
      </a>
    ),
    strong: ({ children }) => (
      <span className="font-semibold text-gray-900">{children}</span>
    ),
    em: ({ children }) => (
      <em className="italic text-gray-800">{children}</em>
    ),
    code: ({ children }) => (
      <code className="bg-gray-100 text-red-600 px-2 py-1 rounded text-sm font-mono">
        {children}
      </code>
    ),
  },
  block: {
    p: ({ children }) => (
      <p className="mb-6 text-gray-700 leading-relaxed text-base md:text-lg tracking-wide">
        {children}
      </p>
    ),
    div: ({ children }) => (
      <div className="my-8">{children}</div>
    ),
    h1: ({ children }) => (
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6 mt-12 first:mt-0">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-gray-900 leading-tight mb-5 mt-10 border-l-4 border-orange-500 pl-4">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl md:text-2xl lg:text-3xl font-semibold text-gray-800 leading-tight mb-4 mt-8">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-lg md:text-xl lg:text-2xl font-medium text-gray-800 leading-tight mb-4 mt-6">
        {children}
      </h4>
    ),
    h5: ({ children }) => (
      <h5 className="text-base md:text-lg lg:text-xl font-medium text-gray-700 leading-tight mb-3 mt-5">
        {children}
      </h5>
    ),
    h6: ({ children }) => (
      <h6 className="text-sm md:text-base lg:text-lg font-medium text-gray-700 leading-tight mb-3 mt-4">
        {children}
      </h6>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-orange-400 pl-6 py-4 my-6 bg-orange-50 italic text-gray-700 text-lg">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-outside ml-6 space-y-2 my-6 text-gray-700">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-outside ml-6 space-y-2 my-6 text-gray-700">
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="leading-relaxed text-base md:text-lg pl-2">
        {children}
      </li>
    ),
    number: ({ children }) => (
      <li className="leading-relaxed text-base md:text-lg pl-2">
        {children}
      </li>
    ),
  },
};


const BlogPost = ({blogData, imageUrl}) => {
  const router = useRouter();
  const blogDetails = blogData[0];


  if(!blogDetails?.customForm?.fields){
    blogDetails.customForm = {
      fields: [
        {
          "_key": "d6a4b6ef98d8",
          "_type": "formField",
          "fieldLabel": "Full Name",
          "fieldName": "username",
          "fieldNameV2": {
            "description": "Name of the main person submitting the request",
            "fieldName": "name"
          },
          "fieldType": "input",
          "isRequired": false,
          "placeholder": "Please enter your name"
        },
        {
          "_key": "50d420f22270",
          "_type": "formField",
          "fieldLabel": "Mobile Number",
          "fieldName": "phone",
          "fieldNameV2": {
            "description": "Mobile Number of the person who is submitting the form",
            "fieldName": "phone"
          },
          "fieldType": "input",
          "isRequired": true,
          "placeholder": "Please enter your mobile number"
        },
        {
          "_key": "5a9bc4c129d2",
          "_type": "formField",
          "fieldLabel": "City",
          "fieldName": "city",
          "fieldNameV2": {
            "description": "City",
            "fieldName": "city"
          },
          "fieldType": "dropdown",
          "isRequired": false,
          "options": ["Bengaluru", "Delhi", "Hyderabad", "Pune", "Mumbai", "Others"]
        },
        {
          "_key": "8a41b22052db",
          "_type": "formField",
          "fieldLabel": "Enquiry",
          "fieldName": "pooja",
          "fieldNameV2": {
            "description": "User's Enquiry",
            "fieldName": "enquiry"
          },
          "fieldType": "text",
          "isRequired": true
        }
      ]
    }
  }

  const [isValidForm, setIsValidForm] = useState(false);
  const [optionalFields, setOptionalFields] = useState([]);
  const [showBottomBar, setShowBottomBar] = useState(true);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validForm = () => {
    return Object.keys(formData).every((field) => {
      if (optionalFields.includes(field)) {
        return true;
      }
      return formData[field] && formData[field].value && formData[field].value !== "";
    });
  };
  const initialFormData = {};
  const transformedFields = transformFields(blogDetails?.customForm?.fields) || {};

  Object.keys(transformedFields).forEach((field) => {
    initialFormData[field] = { value: "" };
  });

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (blogDetails?.customForm?.fields) {
      const transformedFields = transformFields(blogDetails.customForm.fields);
      const notRequiredFields = Object.keys(transformedFields).filter(
        (field) => {
          return transformedFields[field].required === false;
        }
      );
      setOptionalFields(notRequiredFields);
    }
  }, [blogDetails]);

  useEffect(() => {
    setIsValidForm(validForm());
  }, [formData, optionalFields]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: { ...prev[e.target.name], value: e.target.value },
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent multiple clicks
    setIsSubmitting(true);

    try {
      const URL = BACKEND_API_ROUTE + POOJA_LEADS;
      const accessToken = getCookieItem("accessToken");

      const formatBody = {
        unstructuredData: JSON.stringify(formData),
        leadType: 1,
        referenceFrom: router.asPath,
      };

      try {
        const res2 = formatBlogsForm(formData, blogDetails?.customForm?.fields);
        const globalCRMPayload = createCRMLeadEnquiryBody(res2);
        const res = await postGlobalLeadCapture({
          ...globalCRMPayload,
          webPageLink: process.env.NEXT_PUBLIC_SITE_URL+router.asPath
        });
        setFormData({});
      } catch (error) {
        console.error(error);
      }

      const response = await POSTREQUESTAPI(URL, formatBody, accessToken);
      if (response.status === 201) {
        const data = await response.data;
        if (data.statusCode === 200) {
          toast.success(data.message);
          setShowCustomForm(false);
        } else {
          toast.error(data.message);
        }
      } else {
        toast("Something went wrong");
      }
    } catch (error) {
      toast.error("Error posting pooja leads. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!blogDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-800">Blog not found</h1>
          <p className="text-gray-600 mt-2">The blog post you&#39;re looking for doesn&#39;t exist.</p>
        </div>
      </div>
    );
  }
  const openWhatsApp = () => {
    const currentPage = router.asPath;
    const referenceText = `Reference: ${window.location.origin}${currentPage}`;
    const message = encodeURIComponent(
      `Hi! I'd like to chat with a Puja Advisor about a ceremony.\n\n${referenceText}`
    );
    window.open(`https://wa.me/919869929292?text=${message}`, '_blank');
  };




  const canonical = process.env.NEXT_PUBLIC_SITE_URL + router.asPath;
  const primaryImage = imageUrl;

  // Function to render content with injected elements
  const renderContentWithInjections = () => {
    const content = blogDetails.blogParagraphsAndContent || [];
    const totalContent = content.length;
    const customFormPosition = Math.floor(totalContent * 0.3); // Insert at 30% of content
    const connectButtonPosition = Math.floor(totalContent * 0.6); // Insert at 60% of content
    const poojaBoxesPosition = Math.floor(totalContent * 0.8); // Insert at 80% of content

    return content.map((item, index) => (
      <React.Fragment key={index}>
        {/* Render content item */}
        <div className="mb-10">
          {/* Content Images */}
          {item.image && (
            <div className="my-8">
                <div className="relative rounded-lg overflow-hidden shadow-lg aspect-video">
                    <ImageWrapper
                      src={toProxiedUrl(urlFor(item.image).quality(80).url())}
                      alt={blogDetails.blogTitle}
                      fill
                      objectFit="cover"
                    />
                </div>
            </div>
          )}

          {/* YouTube Videos */}
          {item.youtubelink && (
            <div className="my-8 flex justify-center">
              <div className="w-full max-w-4xl">
                <YoutubeThumbnail videoUrl={item.youtubelink}/>
              </div>
            </div>
          )}

          {/* Text Content */}
          {item.paragrahWithHeading && (
            <div className="space-y-6">
              <PortableText
                value={item.paragrahWithHeading}
                components={components}
              />
            </div>
          )}
        </div>

        {/* Inject Custom Form at 30% */}
        {index === customFormPosition && blogDetails?.customForm?.fields && (
          <section className="my-16">
            <div className="bg-gradient-to-br from-orange-100 via-red-50 to-yellow-100 rounded-3xl p-8 md:p-12 shadow-lg">
              {/* Header */}
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {blogDetails.customFormHeading || "Having doubts or want to book a pooja?"}
                </h2>
                <p className="text-gray-600 text-lg">
                  Fill out the form below and we&#39;ll get back to you soon
                </p>
              </div>

              {/* Form */}
              <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-BluntRed to-red-500 p-6">
                  <h3 className="font-semibold text-xl text-white text-center">
                    Fill Your Details
                  </h3>
                </div>

                <div className="p-6">
                  <div className="space-y-4 max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    {Object.keys(transformFields(blogDetails?.customForm?.fields)).map((key) => {
                      const componentProps = {
                        data: transformFields(blogDetails?.customForm?.fields)[key],
                        value: formData[key]?.value || "",
                        onInputChange: handleInputChange,
                      };
                      switch (transformFields(blogDetails?.customForm?.fields)[key].type) {
                        case "select":
                          return <SelectFieldset key={key} {...componentProps} />;
                        case "multi-select":
                          return <MultiSelectFieldset key={key} {...componentProps} />;
                        case "text":
                          return <InputTextComponent key={key} {...componentProps} />;
                        case "number":
                          return <InputNumberComponent key={key} {...componentProps} />;
                        case "date":
                          return <InputDateComponent key={key} {...componentProps} />;
                        case "datetime":
                          return <InputDateTimeComponent key={key} {...componentProps} />;
                        case "file":
                          return <InputFileComponent key={key} {...componentProps} />;
                        case "textarea":
                          return <InputTextAreaComponent key={key} {...componentProps} />;
                        default:
                          return null;
                      }
                    })}
                  </div>

                  <button
                    disabled={!isValidForm || isSubmitting}
                    type="submit"
                    className={`w-full mt-6 py-3 px-6 rounded-lg font-medium text-sm tracking-wide transition-all duration-300 ${
                      !isValidForm || isSubmitting
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-gradient-to-r from-BluntRed to-red-500 text-white hover:from-BluntRed hover:to-red-600 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                    }`}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Enquiry"}
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Inject Connect Button at 60% */}
        {index === connectButtonPosition && (
          <div className="my-12 flex justify-center">
            <button
              onClick={() => {
                if (blogDetails?.CTA?.ctaType === "whatsapp") {
                  openWhatsApp();
                } else if (blogDetails?.CTA?.ctaType === "phone") {
                  window.open("tel:+919869929292", "_self"); // replace with your number
                } else if (blogDetails?.CTA?.ctaType === "link" && blogDetails?.CTA?.linkUrl) {
                  window.open(blogDetails.linkUrl, "_blank");
                } else {
                  window.location.href = "/";
                }
              }}
              className={`flex items-center gap-3 font-semibold py-4 px-8 rounded-full shadow-lg hover:shadow-xl 
                  transform hover:scale-105 transition-all duration-300 
                  ${
                blogDetails?.CTA?.ctaType === "whatsapp"
                  ? "bg-green-500 hover:bg-green-600 text-white"
                  : blogDetails?.CTA?.ctaType === "phone"
                    ? "bg-blue-500 hover:bg-blue-600 text-white"
                    : blogDetails?.CTA?.ctaType === "link"
                      ? "bg-BluntRed hover:bg-white text-white hover:text-BluntRed"
                      : "bg-gradient-to-r from-BluntRed to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
              }`}
            >
              {blogDetails?.CTA?.ctaType === "whatsapp" && <MessageCircle className="w-5 h-5" />}
              {blogDetails?.CTA?.ctaType === "phone" && <Phone className="w-5 h-5" />}
              {blogDetails?.CTA?.ctaType === "link" && <ExternalLink className="w-5 h-5" />}
              {!blogDetails?.CTA?.ctaType && <ExternalLink className="w-5 h-5" />}

              {blogDetails?.CTA?.title || "Explore Vedic Pandits for Poojas"}
            </button>
          </div>
        )}
      </React.Fragment>
    ));
  };

  return (
    <>
      <Head>
        <title>{blogDetails?.seoTitle || DEFAULT_SEO_VALUES.title}</title>
        <link rel="canonical" href={canonical} />
        <meta name="robots" content={blogDetails?.noIndex ? "noindex, follow" : "index, follow"} />
        <meta name="description" content={blogDetails?.seoDescription || DEFAULT_SEO_VALUES.description} />
        <meta property="og:image" content={primaryImage || DEFAULT_SEO_VALUES.imageLink} />
        <meta name="image" content={primaryImage || DEFAULT_SEO_VALUES.imageLink} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="keywords" content={blogDetails?.seoKeywords?.split(',').join(",") || DEFAULT_SEO_VALUES.keywords} />
        <meta name="author" content={blogDetails?.blogAuthor || DEFAULT_SEO_VALUES.author} />
        <meta property="og:title" content={blogDetails?.seoTitle || DEFAULT_SEO_VALUES.title} />
        <meta property="og:description" content={blogDetails?.seoDescription || DEFAULT_SEO_VALUES.description} />
        <meta property="og:type" content="article" />
        <meta property="og:keywords" content={blogDetails?.seoKeywords?.split(',').join(",") || DEFAULT_SEO_VALUES.keywords} />
        <meta name="facebook-domain-verification" content="82hoqhqt8pbrvz70rapxaav9pnsnpk" />
      </Head>

      {/* Main Container */}
      <article className="min-h-screen bg-white pb-24">
        {/* Breadcrumb Navigation */}
        <nav className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto py-4">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <button
                  onClick={() => router.push('/')}
                  className="text-gray-500 hover:text-orange-600 transition-colors"
                >
                  Home
                </button>
              </li>
              <li className="text-gray-400">/</li>
              <li>
                <button
                  onClick={() => router.push('/vidya')}
                  className="text-gray-500 hover:text-orange-600 transition-colors"
                >
                  Blogs
                </button>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900 font-medium truncate max-w-xs">
                {blogDetails?.blogTitle || 'Current Blog'}
              </li>
            </ol>
          </div>
        </nav>

        {/* Hero Image */}
        {primaryImage && (
          <div className="w-full mb-12">
              <div className="relative h-64 sm:h-80 md:h-96 lg:h-[500px] w-full overflow-hidden">
                  <ImageWrapper
                    src={primaryImage}
                    alt={blogDetails.blogTitle}
                    fill
                    objectFit="cover"
                    priority
                  />
                  {/* Gradient overlay for better text readability if needed */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
          </div>
        )}

        {/* Content Container */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Title Section */}
          <header className="mb-8 mt-4">
            {blogDetails.blogTitle && (
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight text-center mb-6">
                {blogDetails.blogTitle}
              </h1>
            )}

            {/* Decorative divider */}
            <div className="flex justify-center mb-8">
              <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-red-500 rounded"></div>
            </div>

            {/* Summary */}
            {blogDetails.blogShortSummary && (
              <div className="text-lg md:text-xl text-gray-600 leading-relaxed text-center max-w-3xl mx-auto mb-8">
                <PortableText value={blogDetails.blogShortSummary} components={components} />
              </div>
            )}
          </header>

          {/* Author Info */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 py-6 mb-8 border-y border-gray-200">
            {blogDetails.blogAuthorImage && (
              <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-orange-200">
                <ImageWrapper
                  src={toProxiedUrl(urlFor(blogDetails.blogAuthorImage).quality(80).url())}
                  alt="author"
                  fill
                  objectFit="cover"
                />
              </div>
            )}
            <div className="text-center sm:text-left">
              {blogDetails.blogAuthor && (
                <p className="font-medium text-gray-900 text-sm">
                  By {blogDetails.blogAuthor}
                </p>
              )}
              {blogDetails.blogPublishedOn && (
                <p className="text-gray-500 text-sm">
                  Published {blogDetails.blogPublishedOn}
                </p>
              )}
            </div>
          </div>

          {/* Main Content with Injections */}
          <div className="prose prose-lg max-w-none">
            {renderContentWithInjections()}
          </div>

          {/* Additional YouTube Video */}
          {blogDetails.youtubeVideoId && (
            <div className="my-12 flex justify-center">
              <div className="w-full max-w-4xl">
                <YoutubeThumbnail videoId={blogDetails.youtubeVideoId}/>
              </div>
            </div>
          )}

          {/* Decorative separator */}
          <div className="flex justify-center my-12">
            <div className="w-32 h-1 bg-gradient-to-r from-orange-400 to-red-500 rounded"></div>
          </div>

          {/*/!* Related Blogs Section *!/*/}
          {/*<section className="my-16">*/}
          {/*    <div className="text-center mb-8">*/}
          {/*        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">*/}
          {/*            Related Articles*/}
          {/*        </h2>*/}
          {/*        <p className="text-gray-600 text-lg">*/}
          {/*            Continue your spiritual journey with these insights*/}
          {/*        </p>*/}
          {/*    </div>*/}
          {/*    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">*/}
          {/*        {dummyRelatedBlogs.map((blog, index) => (*/}
          {/*          <article*/}
          {/*            key={index}*/}
          {/*            onClick={() => router.push(`/blog/${blog.slug}`)}*/}
          {/*            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 hover:border-orange-200 group"*/}
          {/*          >*/}
          {/*              <div className="h-48 bg-gradient-to-br from-orange-200 to-red-200 flex items-center justify-center">*/}
          {/*                  <span className="text-4xl">📿</span>*/}
          {/*              </div>*/}
          {/*              <div className="p-6">*/}
          {/*                  <h3 className="font-semibold text-xl text-gray-800 mb-3 group-hover:text-orange-600 transition-colors">*/}
          {/*                      {blog.title}*/}
          {/*                  </h3>*/}
          {/*                  <p className="text-gray-600 leading-relaxed">*/}
          {/*                      {blog.summary}*/}
          {/*                  </p>*/}
          {/*                  <div className="mt-4 text-orange-600 font-medium group-hover:text-orange-700">*/}
          {/*                      Read More →*/}
          {/*                  </div>*/}
          {/*              </div>*/}
          {/*          </article>*/}
          {/*        ))}*/}
          {/*    </div>*/}
          {/*</section>*/}

          {/* Associated Poojas Section */}
          {blogDetails.blogPoojaLinks && (
            <section className="my-12 bg-gradient-to-r from-BluntRed to-red-50 rounded-2xl p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Associated Poojas
                </h2>
                <p className="text-gray-600 text-lg">
                  Explore related spiritual services
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {blogDetails.blogPoojaLinks.map((item, index) => (
                  <div key={index} className="transform hover:scale-105 transition-transform duration-300">
                    <ImageBox
                      slug={item.poojaName}
                      name={item.poojaName}
                      imageUrl={"/images/Temple_Services.jpg"}
                      altText={item.poojaName}
                      onClick={() => router.push(item.poojaLink)}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      {/* Bottom Connect Bar */}
      {showBottomBar && (
        <div className="sticky bottom-0 bg-white shadow-md p-4 z-40 md:hidden border-t">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm font-medium text-[#8A1C23]">Ready to book your puja?</div>
              <div className="text-xs text-gray-500">Expert pandits. Hassle Free Packages.</div>
            </div>
            <div className="flex items-center gap-2">
              {/* Call Button */}
              <button
                onClick={() => window.open('tel:+919869929292', '_self')}
                className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white py-1.5 px-2 rounded-md text-xs font-medium flex items-center gap-1 w-[80px] justify-center transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                Call
              </button>

              {/* WhatsApp Button */}
              <button
                onClick={openWhatsApp}
                className="bg-[#25D366] hover:bg-[#128C7E] text-white py-1.5 px-2 rounded-md text-xs font-medium flex items-center gap-1 w-[100px] justify-center transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="flex-shrink-0">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516"/>
                </svg>
                WhatsApp
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export async function getServerSideProps(context) {
  try {
    const {params} = context;
    const {slug} = params;
    const res = await getBlogBySlug(slug);
    if (
      res &&
      res.status === 200 &&
      res.data &&
      res.data.statusCode === 200 &&
      Array.isArray(res.data.body) &&
      res.data.body.length > 0
    ) {
      const imageUrl = res.data.body[0]?.blogHeadingImageUrl;
      return {
        props: {blogData: res.data.body, imageUrl: imageUrl || null},
      };
    }
  } catch (error) {
    console.error('Error fetching category SEO details:', error.message);
  }
  return {
    notFound: true,
  };
}

export default BlogPost;